import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Flame, AlertCircle } from 'lucide-react';

export default function ProgressReportPrinciplesValidation({ 
  activity, 
  report,
  open, 
  onClose,
  currentUser
}) {
  const queryClient = useQueryClient();
  const [validatorFlags, setValidatorFlags] = useState(report?.flagged_items || []);
  const [validation, setValidation] = useState({
    status: 'validated',
    notes: '',
    xp_bonus: 0,
    sp_bonus: 0,
    xp_malus: 0,
    sp_malus: 0
  });

  const validateMutation = useMutation({
    mutationFn: async (data) => {
      // Update the progress report entity with validation
      await base44.entities.ProgressReport.update(report.id, {
        principles_validation: {
          status: data.status,
          notes: data.notes,
          validator_flags: data.validator_flags,
          xp_bonus: data.xp_bonus,
          sp_bonus: data.sp_bonus,
          xp_malus: data.xp_malus,
          sp_malus: data.sp_malus
        }
      });

      // Apply XP/SP changes to responsible officer
      const officer = await base44.entities.OfficerProfile.list();
      const responsibleOfficer = officer.find(o => o.user_email === activity.responsible_email);
      
      if (responsibleOfficer) {
        const netXP = (data.xp_bonus || 0) - (data.xp_malus || 0);
        const newTotalXP = (responsibleOfficer.total_xp || 0) + netXP;
        
        await base44.entities.OfficerProfile.update(responsibleOfficer.id, {
          total_xp: Math.max(0, newTotalXP)
        });

        // Update skills with SP
        if (data.sp_bonus || data.sp_malus) {
          const netSP = (data.sp_bonus || 0) - (data.sp_malus || 0);
          // Apply to skills proportionally or to specific skills mentioned in activity
          const updatedSkills = (responsibleOfficer.skills || []).map(skill => ({
            ...skill,
            xp: (skill.xp || 0) + (netSP / (responsibleOfficer.skills?.length || 1))
          }));
          
          await base44.entities.OfficerProfile.update(responsibleOfficer.id, {
            skills: updatedSkills
          });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['progressReports'] });
      queryClient.invalidateQueries({ queryKey: ['officers'] });
      onClose();
    }
  });

  const handleSubmit = () => {
    // Determine status based on flag comparison
    const flagsMatch = 
      validatorFlags.length === (report?.flagged_items || []).length &&
      validatorFlags.every(f => report?.flagged_items?.includes(f));

    validateMutation.mutate({
      status: flagsMatch ? validation.status : 'disputed',
      notes: validation.notes,
      xp_bonus: validation.xp_bonus,
      sp_bonus: validation.sp_bonus,
      xp_malus: validation.xp_malus,
      sp_malus: validation.sp_malus,
      validator_flags: validatorFlags
    });
  };

  const toggleValidatorFlag = (item) => {
    if (validatorFlags.includes(item)) {
      setValidatorFlags(validatorFlags.filter(f => f !== item));
    } else {
      setValidatorFlags([...validatorFlags, item]);
    }
  };

  // Get all items (principles + applicable red lines)
  const allPrinciples = activity?.principles_adhered || [];
  const applicableRedLines = (activity?.red_line_considerations || [])
    .filter(rl => rl.status !== 'not_applicable')
    .map(rl => rl.red_line);
  const allItems = [...allPrinciples, ...applicableRedLines];

  const reportedFlags = report?.flagged_items || [];
  const flagsMatch = 
    validatorFlags.length === reportedFlags.length &&
    validatorFlags.every(f => reportedFlags.includes(f));

  const netXP = (validation.xp_bonus || 0) - (validation.xp_malus || 0);
  const netSP = (validation.sp_bonus || 0) - (validation.sp_malus || 0);

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="cmdr-progressreportprinciplesvalidation-dialog" className="bg-slate-900 text-white border-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-400" />
            Validate Progress Report Principles Adherence
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Officer's Report Summary */}
          <div className="p-4 bg-slate-950/50 rounded-lg border border-slate-800">
            <h3 className="text-sm font-semibold text-slate-400 mb-3">Officer's Report</h3>
            <div className="space-y-2">
              <div>
                <span className="text-xs text-slate-500">Reported by:</span>
                <span className="text-sm text-slate-300 ml-2">{report?.reported_by}</span>
              </div>
              {report?.narrative_on_principles && (
                <div>
                  <span className="text-xs text-slate-500">Narrative:</span>
                  <p className="text-sm text-slate-300 mt-1">{report.narrative_on_principles}</p>
                </div>
              )}
            </div>
          </div>

          {/* Flags Comparison */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Principles & Red Lines Adherence</Label>
              {!flagsMatch && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Flags Don't Match - Will Dispute
                </Badge>
              )}
              {flagsMatch && (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                  Flags Match - Can Validate
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-400">
              Review officer's flags and adjust yours. If flags match exactly, you can validate. Otherwise, it will be disputed.
            </p>

            <div className="space-y-2">
              {allItems.map((item, idx) => {
                const isRedLine = applicableRedLines.includes(item);
                const reportedFlagged = reportedFlags.includes(item);
                const validatorFlagged = validatorFlags.includes(item);
                const flagMismatch = reportedFlagged !== validatorFlagged;

                return (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg border ${
                      flagMismatch 
                        ? 'bg-amber-500/10 border-amber-500/30' 
                        : 'bg-slate-950/50 border-slate-800'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {isRedLine ? (
                            <Flame className="w-4 h-4 text-rose-400 flex-shrink-0" />
                          ) : (
                            <Shield className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                          )}
                          <span className="text-sm text-slate-300">{item}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {/* Officer's flag */}
                        <div className="text-center">
                          <div className="text-xs text-slate-500 mb-1">Officer</div>
                          {reportedFlagged ? (
                            <Badge className="bg-amber-500/30 text-amber-300 border-amber-500/50 text-xs">
                              Struggling
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-slate-700 text-slate-500 text-xs">
                              OK
                            </Badge>
                          )}
                        </div>

                        {/* Validator's flag */}
                        <div className="text-center">
                          <div className="text-xs text-slate-500 mb-1">You</div>
                          <button
                            onClick={() => toggleValidatorFlag(item)}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                              validatorFlagged
                                ? 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
                                : 'bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700'
                            }`}
                          >
                            {validatorFlagged ? 'Struggling' : 'OK'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Validation Notes */}
          <div className="space-y-2">
            <Label className="text-slate-300">Validation Notes</Label>
            <Textarea data-testid="cmdr-progressreportprinciplesvalidation-textarea"
              value={validation.notes}
              onChange={(e) => setValidation({ ...validation, notes: e.target.value })}
              className="bg-slate-950 border-slate-800 min-h-[80px]"
              placeholder="Provide feedback on principles adherence..."
            />
          </div>

          {/* XP/SP Adjustments */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-green-400 mb-2 block">Bonuses</Label>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-slate-400">XP Bonus</Label>
                  <Input data-testid="cmdr-text-xs-text-slate-400-input"
                    type="number"
                    min="0"
                    value={validation.xp_bonus}
                    onChange={(e) => setValidation({ ...validation, xp_bonus: parseInt(e.target.value) || 0 })}
                    className="bg-slate-950 border-slate-800 h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">SP Bonus</Label>
                  <Input data-testid="cmdr-text-xs-text-slate-400-input-1"
                    type="number"
                    min="0"
                    value={validation.sp_bonus}
                    onChange={(e) => setValidation({ ...validation, sp_bonus: parseInt(e.target.value) || 0 })}
                    className="bg-slate-950 border-slate-800 h-9"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm text-red-400 mb-2 block">Penalties</Label>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-slate-400">XP Malus</Label>
                  <Input data-testid="cmdr-text-xs-text-slate-400-input-2"
                    type="number"
                    min="0"
                    value={validation.xp_malus}
                    onChange={(e) => setValidation({ ...validation, xp_malus: parseInt(e.target.value) || 0 })}
                    className="bg-slate-950 border-slate-800 h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">SP Malus</Label>
                  <Input data-testid="cmdr-text-xs-text-slate-400-input-3"
                    type="number"
                    min="0"
                    value={validation.sp_malus}
                    onChange={(e) => setValidation({ ...validation, sp_malus: parseInt(e.target.value) || 0 })}
                    className="bg-slate-950 border-slate-800 h-9"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Net Impact */}
          <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-400">Net XP Impact:</span>
              <span className={`font-semibold ${netXP >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {netXP >= 0 ? '+' : ''}{netXP} XP
              </span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-slate-400">Net SP Impact:</span>
              <span className={`font-semibold ${netSP >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {netSP >= 0 ? '+' : ''}{netSP} SP
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button data-testid="cmdr-cancel-btn" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              className={`${
                flagsMatch 
                  ? 'bg-emerald-600 hover:bg-emerald-700' 
                  : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              {flagsMatch ? 'Validate' : 'Dispute'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </ConfirmableDialog>
  );
}